import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../../assets/css/componentStyles/AuctionImageCarousel.css';
import cameraIcon from '../../../assets/img/icons/camera.svg';
import videoIcon from '../../../assets/img/icons/video.svg';
import leftArrowIcon from '../../../assets/img/icons/carousel-icon-left.svg';
import rightArrowIcon from '../../../assets/img/icons/carousel-icon-right.svg';
import logoMuted from '../../../assets/img/icons/logo-muted.svg';
import { ViewType } from './AuctionListTemplate';

function clampIndex(i, len) {
  if (len === 0) return 0;
  return ((i % len) + len) % len;
}

export default function AuctionImageCarousel({
  photos = [],
  videos = [],
  className = '',
  height = 260,
  width = 320,
  viewType,
}) {
  const [idx, setIdx] = useState(0);

  // fade state
  const [baseUrl, setBaseUrl] = useState('');
  const [overlayUrl, setOverlayUrl] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const FADE_MS = 250;

  const orderedPhotos = useMemo(
    () => [...(photos || [])].sort((a, b) => (a.sortNumber ?? 0) - (b.sortNumber ?? 0)),
    [photos],
  );

  const count = orderedPhotos.length;
  const hasImages = count > 0;
  const currentPhoto = hasImages ? orderedPhotos[clampIndex(idx, count)] : null;
  const currentUrl = currentPhoto?.thumbnailUrl || currentPhoto?.fileUrl || '';

  // init base on first render (when currentUrl arrives)
  useEffect(() => {
    if (baseUrl === '' && currentUrl) setBaseUrl(currentUrl);
  }, [baseUrl, currentUrl]);

  // trigger fade when currentUrl changes
  useEffect(() => {
    if (!currentUrl || currentUrl === baseUrl) return;
    setOverlayUrl(currentUrl);
    setShowOverlay(true);
    const t = setTimeout(() => {
      setBaseUrl(currentUrl);
      setShowOverlay(false);
    }, FADE_MS);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(t);
  }, [currentUrl, baseUrl]);

  const goPrev = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIdx((i) => clampIndex(i - 1, count));
    },
    [count],
  );
  const goNext = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIdx((i) => clampIndex(i + 1, count));
    },
    [count],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={`auction-carousel position-relative overflow-hidden ${className}`}
      style={{
        height,
        borderRadius: viewType === ViewType.LIST ? '12px 0 0 12px' : '12px 12px 0 0',
        width,
        userSelect: 'none',
      }}
    >
      {hasImages ? (
        <>
          {/* image layers */}
          <div
            className="slide base"
            style={{
              backgroundImage: baseUrl ? `url(${baseUrl})` : 'none',
            }}
          />
          <div
            className={`slide overlay ${showOverlay ? 'show' : ''}`}
            style={{
              backgroundImage: overlayUrl ? `url(${overlayUrl})` : 'none',
              transitionDuration: `${FADE_MS}ms`,
            }}
          />
        </>
      ) : (
        <div className="no-image-fallback d-flex justify-content-center align-items-center h-100 w-100">
          <img
            src={logoMuted}
            alt="No images available"
            style={{ maxWidth: '50%', maxHeight: '50%' }}
          />
        </div>
      )}

      {/* top-left counters */}
      <div className="position-absolute top-0 start-0 d-flex gap-2 p-2">
        {hasImages && (
          <div className="carousel-pill d-flex align-items-center gap-1">
            <img src={cameraIcon} alt="camera icon" className="carousel-icon" />
            <span className="carousel-pill-text">{count}</span>
          </div>
        )}
        {Boolean(videos.length) && (
          <div className="carousel-pill d-flex align-items-center gap-1">
            <img src={videoIcon} alt="video icon" className="carousel-icon" />
            <span className="carousel-pill-text">{videos.length}</span>
          </div>
        )}
      </div>

      {/* left/right arrows */}
      {hasImages && count > 1 && (
        <>
          <button
            type="button"
            className="carousel-arrow left"
            aria-label="Previous image"
            onClick={goPrev}
          >
            <img src={leftArrowIcon} alt="left arrow icon" className="arrow-icon" />
          </button>
          <button
            type="button"
            className="carousel-arrow right"
            aria-label="Next image"
            onClick={goNext}
          >
            <img src={rightArrowIcon} alt="right arrow icon" className="arrow-icon" />
          </button>
        </>
      )}
    </div>
  );
}
